import {
  createNativeStackNavigator,
  NativeStackNavigationProp,
} from "@react-navigation/native-stack";
import {
  UserLoggedInNavigatorNavigationProp,
  UserLoggedInNavigatorScreenProps,
} from "@/src/TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { CompositeNavigationProp, RouteProp } from "@react-navigation/native";
import { withWebModal } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModal";
import { CalendarAdminComponent } from "../CalendarCreate/CalendarAdminComponent";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { useCallback, useLayoutEffect, useState } from "react";
import { useAlert } from "@/src/common/Alert";
import { useIntl } from "react-intl";
import { LazyQueryNetworkBoundrary } from "@/src/components/mobile/QueryNetworkBoundrary";
import { CalendarEditQuery } from "./__generated__/CalendarEditQuery.graphql";
import { useNavigationHeaderOptions } from "@/src/common/navigationHeaderOptions";
import { useIntlTokens } from "@/src/components/mobile/useIntlTokens";
import { CalendarEditArchiveMutation } from "./__generated__/CalendarEditArchiveMutation.graphql";
import { CalendarEditDeleteMutation } from "./__generated__/CalendarEditDeleteMutation.graphql";
import { CalendarEditUnarchiveMutation } from "./__generated__/CalendarEditUnarchiveMutation.graphql";

type ParamList = {
  CalendarEditScreen: { calendarId: string; fromSubscribeToCalendar?: boolean };
};

const Stack = createNativeStackNavigator<ParamList>();

function CalendarEditScreen(props: {
  navigation: CompositeNavigationProp<
    NativeStackNavigationProp<ParamList, "CalendarEditScreen">,
    UserLoggedInNavigatorNavigationProp<"CalendarEdit">
  >;
  route: RouteProp<ParamList, "CalendarEditScreen">;
}) {
  return (
    <LazyQueryNetworkBoundrary>
      {(fetchKey) => (
        <CalendarEditScreenComponent fetchKey={fetchKey} {...props} />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

function CalendarEditScreenComponent(
  props: {
    navigation: CompositeNavigationProp<
      NativeStackNavigationProp<ParamList, "CalendarEditScreen">,
      UserLoggedInNavigatorNavigationProp<"CalendarEdit">
    >;
    route: RouteProp<ParamList, "CalendarEditScreen">;
  } & { fetchKey: string }
) {
  const data = useLazyLoadQuery<CalendarEditQuery>(
    graphql`
      query CalendarEditQuery($id: ID!) {
        store {
          ...CalendarAdminComponent_store
          calendar(id: $id) {
            id
            title
            color
            iCalUrl
            isExternal
            isArchived
            teams {
              id
            }
          }
        }
      }
    `,
    {
      id: props.route.params.calendarId,
    },
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );
  const [title, setTitle] = useState(data.store?.calendar?.title ?? "");
  const [teamIds, setTeamIds] = useState(
    data.store?.calendar?.teams.map((team) => team.id) ?? []
  );
  const [color, setColor] = useState(data.store?.calendar?.color ?? "");

  const [updateCalendar, updateCalendarLoading] = useMutation(graphql`
    mutation CalendarEditMutation(
      $calendarId: ID!
      $input: StoreCalendarUpdateInput!
    ) {
      store {
        calendar(calendarId: $calendarId) {
          updateCalendar(input: $input) {
            id
            title
            color
            teams {
              id
            }
            iCalUrl
          }
        }
      }
    }
  `);

  const [deleteCalendar] = useMutation<CalendarEditDeleteMutation>(graphql`
    mutation CalendarEditDeleteMutation($calendarId: ID!) {
      store {
        calendar(calendarId: $calendarId) {
          delete {
            id
            store {
              calendarAddSuggestions {
                iCalUrl
                title
              }
            }
          }
        }
      }
    }
  `);

  const onDelete = useCallback(() => {
    deleteCalendar({
      variables: {
        calendarId: props.route.params.calendarId,
      },
      onCompleted: () => props.navigation.goBack(),
    });
  }, [deleteCalendar, props.navigation, props.route.params.calendarId]);

  const [archiveCalendar,] =
    useMutation<CalendarEditArchiveMutation>(graphql`
      mutation CalendarEditArchiveMutation($calendarId: ID!) {
        store {
          calendar(calendarId: $calendarId) {
            archive {
              id
              isArchived
            }
          }
        }
      }
    `);

  const [unarchiveCalendar,] =
    useMutation<CalendarEditUnarchiveMutation>(graphql`
      mutation CalendarEditUnarchiveMutation($calendarId: ID!) {
        store {
          calendar(calendarId: $calendarId) {
            unarchive {
              id
              isArchived
            }
          }
        }
      }
    `);

  const onArchive = useCallback(() => {
    archiveCalendar({
      variables: {
        calendarId: props.route.params.calendarId,
      },
      onCompleted: () => props.navigation.goBack(),
    });
  }, [archiveCalendar, props.navigation, props.route.params.calendarId]);

  const onUnarchive = useCallback(() => {
    unarchiveCalendar({
      variables: {
        calendarId: props.route.params.calendarId,
      },
      onCompleted: () => props.navigation.goBack(),
    });
  }, [unarchiveCalendar, props.navigation, props.route.params.calendarId]);

  const intl_tokens = useIntlTokens();
  const alert = useAlert();
  const intl = useIntl();

  const openDeleteAlert = useCallback(() => {
    alert.alert(
      intl_tokens.Delete,
      intl.formatMessage({
        defaultMessage: "Are you sure you want to delete this calendar?",
        description: "CalendarEdit: Delete calendar confirmation message",
      }),
      [
        { text: intl_tokens.Cancel, style: "cancel" },
        { text: intl_tokens.Delete, style: "destructive", onPress: onDelete },
      ]
    );
  }, [alert, intl_tokens.Delete, intl_tokens.Cancel, intl, onDelete]);

  const openArchiveAlert = useCallback(() => {
    alert.alert(
      intl_tokens.Archive,
      intl.formatMessage({
        defaultMessage: "Are you sure you want to archive this calendar?",
        description: "CalendarEdit: Archive calendar confirmation message",
      }),
      [
        { text: intl_tokens.Cancel, style: "cancel" },
        { text: intl_tokens.Archive, style: "destructive", onPress: onArchive },
      ]
    );
  }, [alert, intl_tokens.Archive, intl_tokens.Cancel, intl, onArchive]);

  const getHeaderOptions = useNavigationHeaderOptions();

  useLayoutEffect(() => {
    props.navigation.setOptions(
      props.route.params.fromSubscribeToCalendar
        ? getHeaderOptions({
            type: "DONE",
            onDone: () => {
              updateCalendar({
                variables: {
                  calendarId: props.route.params.calendarId,
                  input: {
                    title,
                    teamIds,
                    color,
                  },
                },
                onCompleted: () => props.navigation.goBack(),
              });
            },
            title: intl_tokens.Calendar,
            defaultContentStyle: true,
          })
        : getHeaderOptions({
            type: "SAVE",
            onCancel: () => props.navigation.goBack(),
            onSave: () => {
              updateCalendar({
                variables: {
                  calendarId: props.route.params.calendarId,
                  input: {
                    title,
                    teamIds,
                    color,
                  },
                },
                onCompleted: () => props.navigation.goBack(),
              });
            },
            more: data.store?.calendar?.isExternal
              ? [
                  {
                    label: intl_tokens.Delete,
                    color: "negative",
                    icon: "trash",
                    type: "button",
                    onPress: openDeleteAlert,
                  },
                ]
              : [
                  {
                    label: data.store?.calendar?.isArchived
                      ? intl_tokens.Unarchive
                      : intl_tokens.Archive,
                    color: "text-secondary",
                    icon: "archive",
                    type: "button",
                    onPress: data.store?.calendar?.isArchived
                      ? onUnarchive
                      : openArchiveAlert,
                  },
                ],
            saveLoading: updateCalendarLoading,
            title: intl_tokens.Calendar,
            defaultContentStyle: true,
          })
    );
  }, [
    color,
    data.store?.calendar?.isExternal,
    getHeaderOptions,
    intl_tokens.Calendar,
    intl_tokens.Unarchive,
    intl_tokens.Archive,
    intl_tokens.Delete,
    intl_tokens.EditCalendar,
    openArchiveAlert,
    openDeleteAlert,
    props.navigation,
    props.route.params.calendarId,
    props.route.params.fromSubscribeToCalendar,
    teamIds,
    title,
    updateCalendar,
    updateCalendarLoading,
    data.store?.calendar?.isArchived,
    onUnarchive,
  ]);

  return (
    <CalendarAdminComponent
      title={title}
      setTitle={setTitle}
      iCalUrl={data.store?.calendar?.iCalUrl ?? null}
      teamIds={teamIds}
      setTeamIds={setTeamIds}
      store={data.store}
      color={color}
      setColor={setColor}
    />
  );
}
export const CalendarEdit = withWebModal(
  (props: UserLoggedInNavigatorScreenProps<"CalendarEdit">) => {
    const navigationHeaderOptions = useNavigationHeaderOptions();
    const intl_tokens = useIntlTokens();
    return (
      <Stack.Navigator initialRouteName="CalendarEditScreen">
        <Stack.Screen
          name="CalendarEditScreen"
          component={CalendarEditScreen}
          initialParams={{
            calendarId: props.route.params.calendarId,
            fromSubscribeToCalendar: props.route.params.fromSubscribeToCalendar,
          }}
          options={navigationHeaderOptions({
            type: "CANCEL",
            onClose: () => props.navigation.goBack(),
            title: intl_tokens.Calendar,
            defaultContentStyle: true,
          })}
        />
      </Stack.Navigator>
    );
  }
);
